import type { ClientConfig } from '@/api/generated/types'

/**
 * ФИЛЬТРЫ ПОИСКА — поля спеки, которые решают, КОГО искать: пикеры рельсы и шагов 2–5 плюс штамп подбора
 * (targetingRules.ts). Остальное в спеке — продукт (url, sells, сегменты, контакт), и оно живёт отдельно:
 * «My company» правит продукт, рельса — фильтры. Существительные (`objects`, `objectsFor`) сюда не входят —
 * они не переживают сессию и меряются заново под каждый прогон.
 */
export const STRUCTURAL_FIELDS = [
  'titles',
  'func',
  'sen',
  'indIn',
  'indEx',
  'geo',
  'geoPerson',
  'sizes',
  'cotype',
  'kwin',
  'exkw',
  'titleveto',
  'dmtitles',
  'targetedFor',
] as const satisfies readonly (keyof ClientConfig)[]

export const structuralOf = (spec: Partial<ClientConfig>): Partial<ClientConfig> => {
  const out: Partial<ClientConfig> = {}
  for (const field of STRUCTURAL_FIELDS) {
    if (spec[field] !== undefined) (out as Record<string, unknown>)[field] = spec[field]
  }
  return out
}

/**
 * Пустое — одно и то же, как бы оно ни было записано: поля нет, `[]`, `''`, `{}`, `false`. Снимок записи и
 * черновик окна пишут пустоту по-разному (EMPTY_SPEC подставляет `[]` там, где в записи поля нет), и без этого
 * «фильтры изменены» горело бы сразу при открытии. Штамп подбора — не фильтр, и разницей не считается.
 */
const normalised = (value: unknown): unknown => {
  if (value == null || value === '' || value === false) return null
  if (Array.isArray(value)) return value.length ? value : null
  if (typeof value === 'object') return Object.keys(value as object).length ? value : null
  return value
}

/** Одни и те же фильтры — по полям, что уходят в прогон; порядок значений — тоже разница. */
export const sameFilters = (a: Partial<ClientConfig>, b: Partial<ClientConfig>): boolean => {
  const shape = (spec: Partial<ClientConfig>) =>
    JSON.stringify(STRUCTURAL_FIELDS.filter((field) => field !== 'targetedFor').map((field) => normalised(spec[field])))
  return shape(a) === shape(b)
}

/** Тот же продукт: по адресу, когда он есть у обоих, иначе по тому, что продаёт. */
const sameProduct = (a: Partial<ClientConfig>, b: Partial<ClientConfig>): boolean => {
  const urlA = String(a.url ?? '').trim()
  const urlB = String(b.url ?? '').trim()
  if (urlA && urlB) return urlA === urlB
  return String(a.sells ?? '').trim() === String(b.sells ?? '').trim()
}

/**
 * ФИЛЬТРЫ, С КОТОРЫМИ ПОШЁЛ ПОИСК, СТАНОВЯТСЯ РАБОЧИМИ (Дима, 2026-10-15). До этого окно New search
 * открывалось с фильтрами ПЕРВОГО поиска: рельса правила снимок записи, рабочая спека (`loadSpec`) не менялась,
 * и каждая новая попытка начиналась с исходного списка — Майк: «it keeps reverting back to my original list».
 * Только для того же продукта: поиск старой записи с другим продуктом не должен переписать фильтры нового.
 */
export const adoptSearchFilters = (working: Partial<ClientConfig>, ran: Partial<ClientConfig>): Partial<ClientConfig> =>
  sameProduct(working, ran) ? { ...working, ...structuralOf(ran) } : working
